import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar,
} from "recharts";
import axios from "axios";
import API_BASE_URL from "../config";
import ManagerNavbar from "../Layout/ManagerNavbar";
import {
  FaClipboardCheck, FaChevronDown, FaChevronRight,
  FaTasks, FaCheckCircle, FaClock, FaExclamationTriangle,
  FaUserSlash, FaFire, FaBolt, FaUsers, FaTh,
} from "react-icons/fa";
import { BsLightningChargeFill } from "react-icons/bs";

// ─── Design tokens ────────────────────────────────────────────
const T = {
  bg:       "#f8fafc",
  white:    "#ffffff",
  border:   "#e2e8f0",
  shadow:   "0 1px 3px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
  shadowMd: "0 4px 12px rgba(15,23,42,0.08)",
  radius:   12,
  text1:    "#0f172a",
  text2:    "#475569",
  text3:    "#94a3b8",
  primary:  "#6366f1",
};

const OPEN_STATUSES = ["PENDING", "IN_PROGRESS", "READY TO REVIEW", "READY TO TEST", "IN TESTING"];
const TOP_TABS   = ["Dashboard", "Team"];
const DASH_TABS  = ["Overview", "Weekly Trends"];
const TIME_OPTS  = ["All", "6M", "3M", "1M", "1W"];
const TIME_DAYS  = { "6M": 180, "3M": 90, "1M": 30, "1W": 7 };

const STATUS_COLORS = {
  PENDING:           "#f59e0b",
  IN_PROGRESS:       "#3b82f6",
  "READY TO REVIEW": "#8b5cf6",
  "READY TO TEST":   "#f97316",
  "IN TESTING":      "#06b6d4",
  COMPLETED:         "#10b981",
  CANCELLED:         "#9ca3af",
  REVIEW:            "#8b5cf6",
};
const PRIORITY_COLORS = { High: "#ef4444", Medium: "#f59e0b", Low: "#10b981" };
const TYPE_COLORS    = ["#6366f1","#8b5cf6","#ec4899","#f43f5e","#f59e0b","#10b981","#06b6d4","#3b82f6"];

const STATUS_BADGE = {
  PENDING:           { bg: "#fffbeb", color: "#92400e" },
  IN_PROGRESS:       { bg: "#eff6ff", color: "#1d4ed8" },
  "READY TO REVIEW": { bg: "#f5f3ff", color: "#6d28d9" },
  "READY TO TEST":   { bg: "#fff7ed", color: "#c2410c" },
  "IN TESTING":      { bg: "#ecfeff", color: "#0e7490" },
  COMPLETED:         { bg: "#f0fdf4", color: "#166534" },
  CANCELLED:         { bg: "#f8fafc", color: "#64748b" },
  REVIEW:            { bg: "#f5f3ff", color: "#6d28d9" },
};
const PRIORITY_BADGE = {
  High:   { bg: "#fef2f2", color: "#dc2626" },
  Medium: { bg: "#fffbeb", color: "#b45309" },
  Low:    { bg: "#f0fdf4", color: "#166534" },
};

// ─── Normalizers (fix inconsistent casing / typos from API) ──
const PRIORITY_NORM = { HIGH: "High", MEDIUM: "Medium", LOW: "Low" };
function normalizePriority(p) {
  if (!p) return "None";
  return PRIORITY_NORM[p.toUpperCase()] || p;
}
const STATUS_NORM = { "REAY TO TEST": "READY TO TEST", "REVIEW": "READY TO REVIEW" };
function normalizeStatus(s) {
  if (!s) return s;
  const t = s.trim();
  return STATUS_NORM[t] || t;
}

// ─── Helpers ──────────────────────────────────────────────────
function isoWeekLabel(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dn = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dn);
  const ys = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return `${d.getUTCFullYear()}-W${String(Math.ceil(((d - ys) / 86400000 + 1) / 7)).padStart(2, "0")}`;
}
function buildWeekBuckets(n) {
  const now = new Date(), buckets = {}, order = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now); d.setDate(d.getDate() - i * 7);
    const lb = isoWeekLabel(d);
    if (!(lb in buckets)) { buckets[lb] = null; order.push(lb); }
  }
  return { buckets, order };
}

// ─── Micro-components ─────────────────────────────────────────
const SLabel = ({ children }) => (
  <span style={{ fontSize: "0.65rem", fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.08em" }}>
    {children}
  </span>
);

const StatCard = ({ label, value, sub, accent, Icon }) => (
  <div style={{
    background: T.white, borderRadius: T.radius, padding: "18px 20px 16px",
    boxShadow: T.shadow, borderTop: `3px solid ${accent}`, height: "100%",
    transition: "box-shadow 0.2s",
  }}
    onMouseEnter={e => e.currentTarget.style.boxShadow = T.shadowMd}
    onMouseLeave={e => e.currentTarget.style.boxShadow = T.shadow}
  >
    <div className="d-flex align-items-start justify-content-between mb-2">
      <SLabel>{label}</SLabel>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: accent + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color={accent} />
      </div>
    </div>
    <div style={{ fontSize: "2rem", fontWeight: 800, color: T.text1, lineHeight: 1.1, marginBottom: 4 }}>{value}</div>
    <div style={{ fontSize: "0.73rem", color: T.text3 }}>{sub}</div>
  </div>
);

const Chip = ({ children, bg, color }) => (
  <span style={{ background: bg, color, borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem", fontWeight: 700, display: "inline-block" }}>
    {children}
  </span>
);

const Card = ({ children, style = {} }) => (
  <div style={{ background: T.white, borderRadius: T.radius, padding: "20px 24px", boxShadow: T.shadow, ...style }}>
    {children}
  </div>
);

const CardHead = ({ label, right }) => (
  <div className="d-flex align-items-center justify-content-between mb-4">
    <SLabel>{label}</SLabel>
    {right}
  </div>
);

const ttStyle = {
  contentStyle: { fontSize: 12, borderRadius: 8, border: `1px solid ${T.border}`, boxShadow: T.shadowMd, padding: "8px 12px", color: T.text2 },
  labelStyle:   { fontWeight: 700, color: T.text1, marginBottom: 2 },
};

// Custom dropdown (no native <select>)
const PillSelect = ({ value, onChange, options, active }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const selected = options.find(o => o.value === value) || options[0];
  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen(v => !v)} style={{
        display: "flex", alignItems: "center", gap: 6, padding: "5px 10px 5px 12px", borderRadius: 6,
        border: `1.5px solid ${active ? T.primary : T.border}`,
        background: active ? "#eef2ff" : T.white,
        color: active ? T.primary : T.text2,
        fontSize: "0.78rem", fontWeight: active ? 600 : 500,
        cursor: "pointer", outline: "none", whiteSpace: "nowrap",
        boxShadow: open ? `0 0 0 3px ${T.primary}22` : "none",
        transition: "all 0.15s",
      }}>
        <span>{selected?.label}</span>
        <FaChevronDown size={9} style={{ color: active ? T.primary : T.text3, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)", flexShrink: 0 }} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: 0,
          minWidth: Math.max(ref.current?.offsetWidth || 0, 160), maxHeight: 260, overflowY: "auto",
          background: T.white, border: `1px solid ${T.border}`, borderRadius: 10,
          boxShadow: "0 8px 24px rgba(15,23,42,0.13), 0 2px 8px rgba(15,23,42,0.06)",
          zIndex: 9999, padding: "5px",
        }}>
          {options.map(opt => {
            const isSel = opt.value === value;
            return (
              <div key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                padding: "7px 10px", borderRadius: 6,
                fontSize: "0.8rem", fontWeight: isSel ? 600 : 400,
                color: isSel ? T.primary : T.text1,
                background: isSel ? "#eef2ff" : "transparent",
                cursor: "pointer", transition: "background 0.1s", whiteSpace: "nowrap",
              }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = T.bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSel ? "#eef2ff" : "transparent"; }}
              >
                <span>{opt.label}</span>
                {isSel && <FaCheckCircle size={10} color={T.primary} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ToggleGroup = ({ options, value, onChange }) => (
  <div style={{ display: "inline-flex", border: `1.5px solid ${T.border}`, borderRadius: 6, overflow: "hidden", background: T.white }}>
    {options.map(opt => (
      <button key={opt} onClick={() => onChange(opt)} style={{
        padding: "4px 12px", fontSize: "0.75rem", border: "none",
        background: value === opt ? T.primary : "transparent",
        color: value === opt ? T.white : T.text3,
        cursor: "pointer", fontWeight: value === opt ? 600 : 500, transition: "all 0.15s",
      }}>{opt}</button>
    ))}
  </div>
);

// ═══════════════════════════════════════════════════════════════
const ManagerTaskLanding = () => {
  const navigate = useNavigate();

  const [tasks,   setTasks]   = useState([]);
  const [loading, setLoading] = useState(true);

  const [activeTopTab,  setActiveTopTab]  = useState("Dashboard");
  const [activeDashTab, setActiveDashTab] = useState("Overview");
  const [tabSwitching,  setTabSwitching]  = useState(false);

  // Global filters
  const [timeRange,     setTimeRange]     = useState("All");
  const [filterProject, setFilterProject] = useState("");

  // Team tab filters
  const [expandedId,       setExpandedId]       = useState(null);
  const [filterAssignee,   setFilterAssignee]   = useState("");
  const [teamStatusFilter, setTeamStatusFilter] = useState("Active");
  const [loadFilter,       setLoadFilter]       = useState("All");
  const [priChips,         setPriChips]         = useState([]);

  // Weekly trends filter
  const [weekTypeFilter, setWeekTypeFilter] = useState("All");

  const switchTopTab = useCallback((tab) => {
    setTabSwitching(true); setActiveTopTab(tab); setExpandedId(null);
    setTimeout(() => setTabSwitching(false), 180);
  }, []);
  const switchDashTab = useCallback((tab) => {
    setTabSwitching(true); setActiveDashTab(tab);
    setTimeout(() => setTabSwitching(false), 180);
  }, []);

  useEffect(() => {
    const token      = localStorage.getItem("token");
    const employeeId = localStorage.getItem("employeeId");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!employeeId) { toast.error("Employee profile not found."); setLoading(false); return; }
    // Manager only sees tasks in projects they belong to — NOT all org tasks
    axios.get(`${API_BASE_URL}/api/tasks/projects-of-employee/${employeeId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => setTasks(res.data.map(t => ({
        ...t,
        priority: normalizePriority(t.priority),
        status: normalizeStatus(t.status),
      }))))
      .catch(() => toast.error("Unable to load task data"))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const projectOptions = useMemo(() => {
    const s = new Set(tasks.filter(t => t.projectName).map(t => t.projectName));
    return [{ value: "", label: "All Projects" }, ...[...s].sort().map(p => ({ value: p, label: p }))];
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let d = tasks;
    if (timeRange !== "All") {
      const cut = new Date(today); cut.setDate(cut.getDate() - TIME_DAYS[timeRange]);
      d = d.filter(t => t.createdAt && new Date(t.createdAt) >= cut);
    }
    if (filterProject) d = d.filter(t => t.projectName === filterProject);
    return d;
  }, [tasks, timeRange, filterProject, today]);

  // All derived stats
  const D = useMemo(() => {
    const T2 = filteredTasks;
    const total     = T2.length;
    const doneList  = T2.filter(t => t.status === "COMPLETED");
    const completed = doneList.length;
    const open      = T2.filter(t => OPEN_STATUSES.includes(t.status)).length;
    const rate      = total > 0 ? Math.round((completed / total) * 100) : 0;
    const overdue   = T2.filter(t => t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;
    const unassigned = T2.filter(t => !t.assigneeId && OPEN_STATUSES.includes(t.status)).length;
    const highPri   = T2.filter(t => t.priority === "High" && OPEN_STATUSES.includes(t.status)).length;
    const critical  = T2.filter(t => t.priority === "High" && t.dueDate && OPEN_STATUSES.includes(t.status) && new Date(t.dueDate) < today).length;

    const statusData   = Object.entries(T2.reduce((a,t)=>{ a[t.status]=(a[t.status]||0)+1; return a; },{})).map(([name,value])=>({name,value}));
    const priorityData = Object.entries(T2.reduce((a,t)=>{ const p=t.priority||"None"; a[p]=(a[p]||0)+1; return a; },{})).map(([name,value])=>({name,value}));
    const typeData     = Object.entries(T2.reduce((a,t)=>{ const tp=t.taskType||"General"; a[tp]=(a[tp]||0)+1; return a; },{})).map(([name,value])=>({name,value}));

    const { buckets: waB, order: waO } = buildWeekBuckets(14);
    waO.forEach(w => { waB[w] = 0; });
    T2.forEach(t => { if (!t.createdAt) return; const lb=isoWeekLabel(new Date(t.createdAt)); if (lb in waB) waB[lb]++; });
    const weeklyData = waO.map(w => ({ week: w, tasks: waB[w] }));

    const { buckets: wtB, order: wtO } = buildWeekBuckets(12);
    const typeSet = new Set();
    wtO.forEach(w => { wtB[w] = {}; });
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wtB)) return;
      const tp = t.taskType || "General"; typeSet.add(tp);
      wtB[lb][tp] = (wtB[lb][tp] || 0) + 1;
    });
    const weeklyByType = wtO.map(w => ({ week: w, ...wtB[w] }));
    const allTypes = [...typeSet];

    const { buckets: wpB, order: wpO } = buildWeekBuckets(12);
    wpO.forEach(w => { wpB[w] = { High:0, Medium:0, Low:0 }; });
    T2.forEach(t => {
      if (!t.createdAt) return;
      const lb = isoWeekLabel(new Date(t.createdAt));
      if (!(lb in wpB)) return;
      const p = t.priority || "Low"; if (wpB[lb][p] !== undefined) wpB[lb][p]++;
    });
    const weeklyByPriority = wpO.map(w => ({ week: w, ...wpB[w] }));

    const aMap = {};
    T2.forEach(t => {
      if (!t.assigneeId) return;
      if (!aMap[t.assigneeId]) aMap[t.assigneeId] = { name: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown", open: 0 };
      if (OPEN_STATUSES.includes(t.status)) aMap[t.assigneeId].open++;
    });
    const topAssignees = Object.values(aMap).sort((a,b) => b.open - a.open).slice(0,8);

    const ago30 = new Date(today); ago30.setDate(ago30.getDate()-30);
    const agingCount = T2.filter(t => OPEN_STATUSES.includes(t.status) && t.createdAt && new Date(t.createdAt) < ago30).length;

    // Team data
    const tMap = {};
    T2.forEach(t => {
      if (!t.assigneeId) return;
      if (!tMap[t.assigneeId]) tMap[t.assigneeId] = {
        id: t.assigneeId,
        name: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown",
        open:0, high:0, medium:0, low:0, overdue:0, critical:0, completed:0, maxAge:0, completedTimes:[],
        firstSeen: t.createdAt ? new Date(t.createdAt) : today,
      };
      const m = tMap[t.assigneeId];
      if (t.createdAt && new Date(t.createdAt) < m.firstSeen) m.firstSeen = new Date(t.createdAt);
      if (OPEN_STATUSES.includes(t.status)) {
        m.open++;
        if (t.priority==="High") m.high++;
        else if (t.priority==="Medium") m.medium++;
        else m.low++;
        if (t.dueDate && new Date(t.dueDate) < today) { m.overdue++; if (t.priority==="High") m.critical++; }
        if (t.createdAt) m.maxAge = Math.max(m.maxAge, Math.floor((today - new Date(t.createdAt)) / 86400000));
      }
      if (t.status==="COMPLETED") m.completed++;
    });
    const teamData = Object.values(tMap).map(m => ({
      ...m,
      daysActive: Math.max(0, Math.floor((today - m.firstSeen) / 86400000)),
      loadScore:  Math.round((m.high*3 + m.medium*1.5 + m.low*0.5)*10)/10,
    })).sort((a,b) => b.open - a.open);

    return { total, open, completed, rate, overdue, unassigned, highPri, critical, agingCount, statusData, priorityData, typeData, weeklyData, weeklyByType, allTypes, weeklyByPriority, topAssignees, teamData };
  }, [filteredTasks, today]);

  const assigneeFirstSeen = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!t.assigneeId || !t.createdAt) return;
      const d = new Date(t.createdAt);
      if (!map[t.assigneeId] || d < map[t.assigneeId]) map[t.assigneeId] = d;
    });
    return map;
  }, [tasks]);

  const assigneeOptions = useMemo(() => {
    const seen = new Set(); const opts = [];
    filteredTasks.forEach(t => {
      if (t.assigneeId && !seen.has(t.assigneeId)) {
        seen.add(t.assigneeId);
        opts.push({ value: String(t.assigneeId), label: [t.assigneeFirstName, t.assigneeLastName].filter(Boolean).join(" ") || "Unknown" });
      }
    });
    return [{ value: "", label: "Any Assignee" }, ...opts.sort((a,b) => a.label.localeCompare(b.label))];
  }, [filteredTasks]);

  const filteredTeamData = useMemo(() => {
    const allMaxScore = D.teamData[0]?.loadScore || 1;
    let d = D.teamData.map(m => {
      const firstSeen = assigneeFirstSeen[m.id];
      const daysActive = firstSeen ? Math.max(0, Math.floor((today - firstSeen) / 86400000)) : 0;
      return { ...m, daysActive, loadPct: allMaxScore > 0 ? Math.round((m.loadScore / allMaxScore)*100) : 0 };
    });
    if (filterAssignee) d = d.filter(m => String(m.id) === filterAssignee);
    if (priChips.length > 0) d = d.filter(m => priChips.every(p => p==="High"?m.high>0:p==="Medium"?m.medium>0:m.low>0));
    if (loadFilter==="Heavy")  d = d.filter(m => m.loadPct > 65);
    if (loadFilter==="Medium") d = d.filter(m => m.loadPct > 35 && m.loadPct <= 65);
    if (loadFilter==="Normal") d = d.filter(m => m.loadPct <= 35);
    if (teamStatusFilter==="Active") d = d.filter(m => m.open > 0);
    return d;
  }, [D.teamData, assigneeFirstSeen, today, filterAssignee, priChips, loadFilter, teamStatusFilter]);

  const maxLoadScore = D.teamData[0]?.loadScore || 1;

  const filteredWeeklyByType = useMemo(() => {
    if (weekTypeFilter==="All") return D.weeklyByType;
    return D.weeklyByType.map(w => ({ week: w.week, [weekTypeFilter]: w[weekTypeFilter]||0 }));
  }, [D.weeklyByType, weekTypeFilter]);

  const getAssigneeTasks = useCallback((id) => {
    return filteredTasks.filter(t => t.assigneeId===id && OPEN_STATUSES.includes(t.status))
      .sort((a,b) => {
        const po = { High:0, Medium:1, Low:2 };
        const diff = (po[a.priority]??3) - (po[b.priority]??3);
        return diff!==0 ? diff : (!a.dueDate?1:!b.dueDate?-1:new Date(a.dueDate)-new Date(b.dueDate));
      });
  }, [filteredTasks]);

  const statCards = [
    { label: "Total Tasks",   value: D.total,     sub: "In your scope",           accent: "#475569", Icon: FaTasks           },
    { label: "Open",          value: D.open,      sub: "Currently active",         accent: "#ea580c", Icon: FaClipboardCheck  },
    { label: "Completed",     value: D.completed, sub: `${D.rate}% completion`,   accent: T.primary, Icon: FaCheckCircle     },
    { label: "Overdue",       value: D.overdue,   sub: "Past due date",            accent: "#dc2626", Icon: FaExclamationTriangle },
    { label: "Unassigned",    value: D.unassigned,sub: "Need an owner",            accent: "#d97706", Icon: FaUserSlash       },
    { label: "High Priority", value: D.highPri,   sub: "Open & high priority",     accent: "#dc2626", Icon: FaFire            },
    { label: "Critical",      value: D.critical,  sub: "Overdue + high priority",  accent: "#9f1239", Icon: FaBolt            },
    { label: "Team Members",  value: D.teamData.filter(m=>m.open>0).length, sub: "With active tasks", accent: "#7c3aed", Icon: FaUsers },
  ];

  const axisProps = { axisLine: false, tickLine: false };
  const xTick = { fontSize: 10, fill: T.text3 };
  const yTick = { fontSize: 10, fill: T.text3 };

  const anyGlobalFilter = timeRange!=="All" || filterProject;
  const anyTeamFilter   = filterAssignee || teamStatusFilter!=="Active" || loadFilter!=="All" || priChips.length>0;

  const NAVBAR_H    = 105;
  const SUBNAV_H    = 44;
  const FILTERBAR_H = 44;
  const DASHTABS_H  = 40;

  const donutChart = ({ label, data, colorFn }) => (
    <div key={label} className="col-12 col-md-4">
      <Card style={{ height:"100%" }}>
        <CardHead label={label} />
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="38%" innerRadius={48} outerRadius={76} paddingAngle={3}>
              {data.map((e,i) => <Cell key={i} fill={colorFn(e,i)} />)}
            </Pie>
            <Tooltip {...ttStyle} />
            <Legend layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize:"0.72rem", lineHeight:"1.9", paddingLeft:8, color:T.text2 }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );

  return (
    <>
      <div className="fixed-top"><ManagerNavbar /></div>

      <div style={{ paddingTop: NAVBAR_H, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ════ SUB-NAV ════ */}
        <div style={{ flexShrink:0, background:T.white, borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 24px", height:SUBNAV_H }}>
          <div className="d-flex align-items-center" style={{ gap:2 }}>
            {TOP_TABS.map(tab => {
              const isActive = activeTopTab===tab;
              const Icon = tab==="Dashboard" ? FaTh : FaUsers;
              return (
                <button key={tab} onClick={() => switchTopTab(tab)} style={{
                  display:"flex", alignItems:"center", gap:6,
                  padding:`0 14px`, height:SUBNAV_H,
                  fontSize:"0.8rem", fontWeight:isActive?700:500,
                  color:isActive?T.primary:T.text2,
                  background:isActive?"#eef2ff":"transparent",
                  border:"none", borderBottom:`2px solid ${isActive?T.primary:"transparent"}`,
                  cursor:"pointer", transition:"all 0.15s", whiteSpace:"nowrap",
                }}>
                  <Icon size={12} />{tab}
                </button>
              );
            })}
          </div>

          <div className="d-flex align-items-center gap-2">
            <Link to="/viewtaskss" style={{
              display:"flex", alignItems:"center", gap:6, padding:"6px 14px", borderRadius:6,
              fontSize:"0.75rem", fontWeight:600, background:T.primary, color:"#fff",
              textDecoration:"none", whiteSpace:"nowrap", transition:"opacity 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.opacity="0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity="1"}
            >
              <FaClipboardCheck size={11} /> Tasks Board
            </Link>
          </div>
        </div>

        {/* ════ FILTER BAR ════ */}
        <div style={{ flexShrink:0, background:T.white, borderBottom:`1px solid ${T.border}`, boxShadow:"0 1px 4px rgba(15,23,42,0.05)" }}>
          <div style={{ padding:"0 24px" }}>

            {/* Dashboard filter bar */}
            {activeTopTab==="Dashboard" && (
              <>
                <div className="d-flex align-items-center justify-content-between" style={{ height:FILTERBAR_H, gap:12 }}>
                  <div className="d-flex align-items-center gap-2" style={{ flexWrap:"wrap" }}>
                    <span style={{ fontSize:"0.68rem", fontWeight:800, color:T.text3, textTransform:"uppercase", letterSpacing:"0.1em", marginRight:6 }}>
                      Dashboard
                    </span>
                    {projectOptions.length > 1 && (
                      <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
                    )}
                    {anyGlobalFilter && (
                      <button onClick={() => { setTimeRange("All"); setFilterProject(""); }}
                        style={{ fontSize:"0.72rem", color:T.text3, background:"none", border:"none", cursor:"pointer", textDecoration:"underline", padding:0 }}>
                        Reset
                      </button>
                    )}
                  </div>
                  <div style={{ display:"inline-flex", border:`1.5px solid ${T.border}`, borderRadius:6, overflow:"hidden", flexShrink:0 }}>
                    {TIME_OPTS.map((opt,i) => (
                      <button key={opt} onClick={() => setTimeRange(opt)} style={{
                        padding:"4px 12px", fontSize:"0.75rem", border:"none",
                        borderLeft:i>0?`1px solid ${T.border}`:"none",
                        background:timeRange===opt?"#0f172a":"transparent",
                        color:timeRange===opt?"#fff":T.text3,
                        cursor:"pointer", fontWeight:timeRange===opt?700:500, transition:"all 0.12s",
                      }}>{opt}</button>
                    ))}
                  </div>
                </div>

                {/* Sub-tabs */}
                <div className="d-flex" style={{ gap:0, borderTop:`1px solid ${T.border}` }}>
                  {DASH_TABS.map(tab => {
                    const isActive = activeDashTab===tab;
                    return (
                      <button key={tab} onClick={() => switchDashTab(tab)} style={{
                        padding:`0 18px`, height:DASHTABS_H, fontSize:"0.82rem",
                        fontWeight:isActive?600:500, color:isActive?T.primary:T.text2,
                        background:"transparent", border:"none",
                        borderBottomWidth:2, borderBottomStyle:"solid", borderBottomColor:isActive?T.primary:"transparent",
                        cursor:"pointer", whiteSpace:"nowrap", transition:"color 0.15s, border-color 0.15s",
                      }}>{tab}</button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Team filter bar */}
            {activeTopTab==="Team" && (
              <div className="d-flex align-items-center gap-3" style={{ padding:"10px 0", flexWrap:"wrap" }}>
                <span style={{ fontSize:"0.68rem", fontWeight:800, color:T.text3, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                  Team
                </span>
                <div style={{ width:1, height:18, background:T.border }} />
                {projectOptions.length > 1 && (
                  <PillSelect value={filterProject} onChange={setFilterProject} options={projectOptions} active={!!filterProject} />
                )}
                <PillSelect value={filterAssignee} onChange={setFilterAssignee} options={assigneeOptions} active={!!filterAssignee} />
                <div style={{ width:1, height:18, background:T.border }} />
                <ToggleGroup options={["Active","All"]} value={teamStatusFilter} onChange={setTeamStatusFilter} />
                {anyTeamFilter && (
                  <button onClick={() => { setFilterAssignee(""); setTeamStatusFilter("Active"); setLoadFilter("All"); setPriChips([]); }}
                    style={{ fontSize:"0.72rem", color:T.text3, background:"none", border:"none", cursor:"pointer", textDecoration:"underline", padding:0, marginLeft:"auto" }}>
                    Reset all
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ════ SCROLLABLE CONTENT ════ */}
        <div className="mgr-scroll" style={{ flex:1, overflowY:"auto", background:T.bg }}>

          {(loading || tabSwitching) ? (
            <div style={{ height:"100%", minHeight:420, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:16 }}>
              <div style={{ position:"relative", width:48, height:48 }}>
                <div className="spinner-border" style={{ width:48, height:48, color:T.primary, opacity:0.18, position:"absolute", top:0, left:0 }} />
                <div className="spinner-border" style={{ width:48, height:48, color:T.primary, position:"absolute", top:0, left:0, borderTopColor:"transparent", borderRightColor:"transparent" }} />
              </div>
              <div style={{ textAlign:"center" }}>
                <p style={{ fontSize:"0.92rem", fontWeight:600, color:T.text1, marginBottom:4 }}>
                  {loading ? `Loading ${activeTopTab==="Team"?"Team":"Dashboard"}` : `Opening ${activeTopTab==="Team"?"Team":activeDashTab}`}
                </p>
                <p style={{ fontSize:"0.78rem", color:T.text3, marginBottom:0 }}>
                  {loading ? "Fetching task data…" : "Preparing view…"}
                </p>
              </div>
            </div>
          ) : (
            <div style={{ padding:"20px 24px" }}>

              {/* Active filter pills */}
              {anyGlobalFilter && activeTopTab==="Dashboard" && (
                <div className="d-flex gap-2 flex-wrap mb-3">
                  {timeRange!=="All" && <span style={{ fontSize:"0.72rem", background:"#eef2ff", color:T.primary, borderRadius:20, padding:"3px 12px", fontWeight:600, border:`1px solid #c7d2fe` }}>Last {timeRange}</span>}
                  {filterProject && <span style={{ fontSize:"0.72rem", background:"#eef2ff", color:T.primary, borderRadius:20, padding:"3px 12px", fontWeight:600, border:`1px solid #c7d2fe` }}>{filterProject}</span>}
                  <span style={{ fontSize:"0.72rem", color:T.text3, padding:"3px 0" }}>{filteredTasks.length} of {tasks.length} tasks</span>
                </div>
              )}

              {/* ── DASHBOARD TAB ── */}
              {activeTopTab==="Dashboard" && (
                <>
                  {/* Overview */}
                  {activeDashTab==="Overview" && (
                    <>
                      <div className="row g-3 mb-4">
                        {statCards.map((c,i) => (
                          <div key={i} className="col-6 col-md-3"><StatCard {...c} /></div>
                        ))}
                      </div>

                      <Card style={{ marginBottom:20 }}>
                        <CardHead label="Task Volume by Week"
                          right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>{filteredTasks.length} tasks · {D.weeklyData.length} weeks</span>}
                        />
                        <ResponsiveContainer width="100%" height={160}>
                          <AreaChart data={D.weeklyData} margin={{ top:4, right:8, left:-24, bottom:0 }}>
                            <defs>
                              <linearGradient id="mgrGrad1" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor={T.primary} stopOpacity={0.15} />
                                <stop offset="95%" stopColor={T.primary} stopOpacity={0}    />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                            <XAxis dataKey="week" tick={xTick} {...axisProps} />
                            <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                            <Tooltip {...ttStyle} />
                            <Area type="monotone" dataKey="tasks" name="Tasks Created" stroke={T.primary} strokeWidth={2}
                              fill="url(#mgrGrad1)" dot={{ fill:T.primary, r:3, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      </Card>

                      <div className="row g-3 mb-4">
                        {[
                          { label:"By Status",    data:D.statusData,   colorFn:(e)   => STATUS_COLORS[e.name]   || "#94a3b8" },
                          { label:"By Priority",  data:D.priorityData, colorFn:(e)   => PRIORITY_COLORS[e.name] || "#94a3b8" },
                          { label:"By Task Type", data:D.typeData,     colorFn:(_,i) => TYPE_COLORS[i%TYPE_COLORS.length]    },
                        ].map(donutChart)}
                      </div>

                      <div className="row g-3">
                        <div className="col-12 col-md-7">
                          <Card style={{ height:"100%" }}>
                            <CardHead label="Top Assignees by Open Tasks"
                              right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>{D.topAssignees.length} members</span>}
                            />
                            {D.topAssignees.length===0
                              ? <p style={{ fontSize:"0.84rem", color:T.text3 }}>No assigned open tasks.</p>
                              : <ResponsiveContainer width="100%" height={Math.max(D.topAssignees.length*38+16, 120)}>
                                  <BarChart data={D.topAssignees} layout="vertical" margin={{ top:0, right:52, left:4, bottom:0 }} barSize={13}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={T.border} />
                                    <XAxis type="number" allowDecimals={false} tick={xTick} {...axisProps} />
                                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize:12, fill:T.text2 }} {...axisProps} />
                                    <Tooltip {...ttStyle} cursor={{ fill:T.bg }} />
                                    <Bar dataKey="open" name="Open Tasks" fill={T.primary} radius={[0,6,6,0]}
                                      label={{ position:"right", fontSize:12, fill:T.text2, fontWeight:700 }} />
                                  </BarChart>
                                </ResponsiveContainer>
                            }
                          </Card>
                        </div>
                        <div className="col-12 col-md-5">
                          <Card style={{ height:"100%" }}>
                            <CardHead label="Aging Tasks › 30 Days" />
                            {D.agingCount===0
                              ? <p style={{ fontSize:"0.84rem", color:T.text3, lineHeight:1.7 }}>All open tasks created within last 30 days. No action needed.</p>
                              : <>
                                  <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:12 }}>
                                    <span style={{ fontSize:"2.5rem", fontWeight:800, color:"#dc2626", lineHeight:1 }}>{D.agingCount}</span>
                                    <span style={{ fontSize:"0.84rem", color:T.text2 }}>open {D.agingCount===1?"task":"tasks"} unresolved &gt; 30 days</span>
                                  </div>
                                  <p style={{ fontSize:"0.82rem", color:T.text3, lineHeight:1.6 }}>Review and reassign or update these tasks.</p>
                                </>
                            }
                            <Link to="/viewtaskss" style={{ fontSize:"0.78rem", color:T.primary, fontWeight:600, textDecoration:"none" }}>
                              Review tasks →
                            </Link>
                          </Card>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Weekly Trends */}
                  {activeDashTab==="Weekly Trends" && (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-4">
                        {["All", ...D.allTypes].map(tp => (
                          <button key={tp} onClick={() => setWeekTypeFilter(tp)} style={{
                            padding:"4px 14px", fontSize:"0.78rem", borderRadius:6, cursor:"pointer",
                            border:`1.5px solid ${weekTypeFilter===tp?T.primary:T.border}`,
                            background:weekTypeFilter===tp?T.primary:T.white,
                            color:weekTypeFilter===tp?T.white:T.text2,
                            fontWeight:weekTypeFilter===tp?600:500, transition:"all 0.15s",
                          }}>{tp}</button>
                        ))}
                      </div>

                      <div className="row g-3 mb-3">
                        <div className="col-12 col-md-6">
                          <Card>
                            <CardHead label="Weekly Category Breakdown" />
                            <ResponsiveContainer width="100%" height={230}>
                              <BarChart data={filteredWeeklyByType} margin={{ top:0, right:8, left:-24, bottom:0 }} barSize={9}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize:"0.72rem", paddingTop:10, color:T.text2 }} iconType="circle" iconSize={7} />
                                {(weekTypeFilter==="All"?D.allTypes:[weekTypeFilter]).map((tp,i) => (
                                  <Bar key={tp} dataKey={tp} stackId="a"
                                    fill={TYPE_COLORS[D.allTypes.indexOf(tp)%TYPE_COLORS.length]}
                                    radius={i===(weekTypeFilter==="All"?D.allTypes:[weekTypeFilter]).length-1?[2,2,0,0]:[0,0,0,0]} />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                        <div className="col-12 col-md-6">
                          <Card>
                            <CardHead label="Weekly by Priority" />
                            <ResponsiveContainer width="100%" height={230}>
                              <BarChart data={D.weeklyByPriority} margin={{ top:0, right:8, left:-24, bottom:0 }} barSize={9}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="week" tick={xTick} {...axisProps} interval={2} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Legend wrapperStyle={{ fontSize:"0.72rem", paddingTop:10, color:T.text2 }} iconType="circle" iconSize={7} />
                                <Bar dataKey="High"   stackId="p" fill="#ef4444" radius={[0,0,0,0]} />
                                <Bar dataKey="Medium" stackId="p" fill="#f59e0b" radius={[0,0,0,0]} />
                                <Bar dataKey="Low"    stackId="p" fill="#10b981" radius={[2,2,0,0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                        <div className="col-12">
                          <Card>
                            <CardHead label="Running Volume (14 weeks)"
                              right={<span style={{ fontSize:"0.72rem", color:T.text3 }}>tasks created per week</span>}
                            />
                            <ResponsiveContainer width="100%" height={155}>
                              <AreaChart data={D.weeklyData} margin={{ top:4, right:8, left:-24, bottom:0 }}>
                                <defs>
                                  <linearGradient id="mgrGrad2" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor={T.primary} stopOpacity={0.14} />
                                    <stop offset="95%" stopColor={T.primary} stopOpacity={0}    />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={T.border} />
                                <XAxis dataKey="week" tick={xTick} {...axisProps} />
                                <YAxis allowDecimals={false} tick={yTick} {...axisProps} />
                                <Tooltip {...ttStyle} />
                                <Area type="monotone" dataKey="tasks" name="Created" stroke={T.primary} strokeWidth={2}
                                  fill="url(#mgrGrad2)" dot={{ fill:T.primary, r:3, strokeWidth:0 }} activeDot={{ r:5, strokeWidth:0 }} />
                              </AreaChart>
                            </ResponsiveContainer>
                          </Card>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── TEAM TAB ── */}
              {activeTopTab==="Team" && (() => {
                const activeCount  = filteredTeamData.filter(m=>m.open>0).length;
                const totalOpen    = filteredTeamData.reduce((s,m)=>s+m.open,0);
                const totalOverdue = filteredTeamData.reduce((s,m)=>s+m.overdue,0);
                const totalCritical= filteredTeamData.reduce((s,m)=>s+m.critical,0);
                const avgLoad      = activeCount>0?(totalOpen/activeCount).toFixed(1):"0";

                const togglePriChip = p => setPriChips(prev => prev.includes(p)?prev.filter(x=>x!==p):[...prev,p]);

                return (
                  <>
                    {/* Summary cards */}
                    <div className="row g-3 mb-4">
                      {[
                        { label:"Active Members", value:activeCount,   sub:"With open tasks",         dotColor:T.text3,    valColor:T.text1   },
                        { label:"Total Open",     value:totalOpen,     sub:"Across team",              dotColor:"#ea580c",  valColor:"#ea580c" },
                        { label:"Overdue",        value:totalOverdue,  sub:"Past due",                 dotColor:"#dc2626",  valColor:"#dc2626" },
                        { label:"Critical",       value:totalCritical, sub:"High priority + overdue",  dotColor:"#dc2626",  valColor:"#dc2626" },
                        { label:"Avg Load",       value:avgLoad,       sub:"Open tasks per person",    dotColor:T.text1,    valColor:T.text1   },
                        { label:"Total Members",  value:D.teamData.length, sub:"In your team",         dotColor:T.primary,  valColor:T.primary },
                      ].map((c,i) => (
                        <div key={i} className="col-6 col-md-2">
                          <div style={{ background:T.white, borderRadius:T.radius, padding:"16px 18px", boxShadow:T.shadow, position:"relative", height:"100%", borderTop:`3px solid ${c.dotColor}` }}>
                            <div style={{ position:"absolute", top:13, right:13, width:8, height:8, borderRadius:"50%", background:c.dotColor }} />
                            <p style={{ fontSize:"0.65rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>{c.label}</p>
                            <p style={{ fontSize:"1.9rem", fontWeight:800, color:c.valColor, lineHeight:1.05, marginBottom:2 }}>{c.value}</p>
                            <p style={{ fontSize:"0.72rem", color:T.text2, marginBottom:0 }}>{c.sub}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Filter bar */}
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3 px-4 py-3"
                      style={{ background:T.white, borderRadius:T.radius, boxShadow:T.shadow, border:`1px solid ${T.border}` }}>
                      <span style={{ fontSize:"0.78rem", color:T.text2, whiteSpace:"nowrap" }}>
                        Showing <strong style={{ color:T.primary }}>{filteredTeamData.length}</strong> members
                      </span>
                      <div style={{ width:1, height:20, background:T.border }} />

                      {/* Load filter */}
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize:"0.72rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em" }}>Load:</span>
                        <div style={{ display:"inline-flex", border:`1.5px solid ${T.border}`, borderRadius:6, overflow:"hidden" }}>
                          {["All","Heavy","Medium","Normal"].map((opt,i) => (
                            <button key={opt} onClick={() => setLoadFilter(opt)} style={{
                              padding:"4px 12px", fontSize:"0.75rem", border:"none",
                              borderLeft:i>0?`1px solid ${T.border}`:"none",
                              background:loadFilter===opt?T.primary:"transparent",
                              color:loadFilter===opt?T.white:T.text2,
                              cursor:"pointer", fontWeight:loadFilter===opt?700:500, transition:"all 0.12s",
                            }}>{opt}</button>
                          ))}
                        </div>
                      </div>
                      <div style={{ width:1, height:20, background:T.border }} />

                      {/* Priority chips */}
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ fontSize:"0.72rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.06em" }}>Priority:</span>
                        <div className="d-flex gap-1">
                          {[
                            { key:"High",   label:"H", bg:"#fef2f2", activeBg:"#dc2626", color:"#dc2626", activeColor:"#fff" },
                            { key:"Medium", label:"M", bg:"#fffbeb", activeBg:"#b45309", color:"#b45309", activeColor:"#fff" },
                            { key:"Low",    label:"L", bg:"#f0fdf4", activeBg:"#166534", color:"#166534", activeColor:"#fff" },
                          ].map(p => {
                            const isA = priChips.includes(p.key);
                            return (
                              <button key={p.key} onClick={() => togglePriChip(p.key)} title={p.key} style={{
                                width:28, height:28, borderRadius:"50%", border:"none",
                                background:isA?p.activeBg:p.bg, color:isA?p.activeColor:p.color,
                                fontSize:"0.72rem", fontWeight:800, cursor:"pointer",
                                display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s",
                              }}>{p.label}</button>
                            );
                          })}
                        </div>
                      </div>

                      {anyTeamFilter && (
                        <button onClick={() => { setFilterAssignee(""); setTeamStatusFilter("Active"); setLoadFilter("All"); setPriChips([]); }}
                          style={{ fontSize:"0.72rem", color:T.text3, background:"none", border:"none", cursor:"pointer", textDecoration:"underline", padding:0, marginLeft:"auto" }}>
                          Reset all
                        </button>
                      )}
                    </div>

                    {/* Team table */}
                    <Card>
                      {filteredTeamData.length===0
                        ? <div className="text-center py-4"><p style={{ fontSize:"0.9rem", color:T.text3, marginBottom:0 }}>No members match the current filters.</p></div>
                        : (
                          <div className="table-responsive">
                            <table className="table table-borderless align-middle mb-0">
                              <thead>
                                <tr style={{ borderBottom:`2px solid ${T.border}` }}>
                                  {["Member","Open ↓","Priority Mix","Load Score","Overdue","Critical","Days Active","Aging","Completed"].map((h,i) => (
                                    <th key={i} style={{ fontSize:"0.67rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.07em", paddingTop:8, paddingBottom:12, whiteSpace:"nowrap" }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTeamData.map(m => {
                                  const pct  = maxLoadScore>0 ? Math.round((m.loadScore/maxLoadScore)*100) : 0;
                                  const bCol = pct>65?"#ef4444":pct>35?"#f59e0b":"#10b981";
                                  const isExp= expandedId===m.id;
                                  const aTasks = isExp ? getAssigneeTasks(m.id) : [];
                                  return (
                                    <React.Fragment key={m.id}>
                                      <tr onClick={() => setExpandedId(p=>p===m.id?null:m.id)}
                                        style={{ borderBottom:`1px solid ${T.border}`, cursor:"pointer", background:isExp?"#fafbff":"transparent", transition:"background 0.12s" }}
                                        onMouseEnter={e => { if (!isExp) e.currentTarget.style.background=T.bg; }}
                                        onMouseLeave={e => { if (!isExp) e.currentTarget.style.background="transparent"; }}
                                      >
                                        <td style={{ paddingTop:12, paddingBottom:12 }}>
                                          <div className="d-flex align-items-center gap-2">
                                            {isExp ? <FaChevronDown size={10} color={T.primary} /> : <FaChevronRight size={10} color={T.text3} />}
                                            <div style={{ width:30, height:30, borderRadius:"50%", background:`${T.primary}20`, color:T.primary, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.73rem", fontWeight:800, flexShrink:0 }}>
                                              {m.name.charAt(0).toUpperCase()}
                                            </div>
                                            <span style={{ fontWeight:600, color:T.text1, fontSize:"0.85rem" }}>{m.name}</span>
                                          </div>
                                        </td>
                                        <td className="text-center"><Chip bg="#eff6ff" color="#2563eb">{m.open}</Chip></td>
                                        <td>
                                          <div className="d-flex gap-1 flex-wrap">
                                            {m.high>0   && <Chip bg="#fef2f2" color="#dc2626">{m.high}</Chip>}
                                            {m.medium>0 && <Chip bg="#fffbeb" color="#b45309">{m.medium}</Chip>}
                                            {m.low>0    && <Chip bg="#f0fdf4" color="#166534">{m.low}</Chip>}
                                            {m.open===0 && <span style={{ color:T.text3, fontSize:"0.75rem" }}>—</span>}
                                          </div>
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center gap-2">
                                            <div style={{ flex:1, height:7, background:T.border, borderRadius:4, overflow:"hidden", minWidth:70 }}>
                                              <div style={{ height:"100%", width:`${pct}%`, background:bCol, borderRadius:4, transition:"width 0.5s" }} />
                                            </div>
                                            <span style={{ fontSize:"0.76rem", fontWeight:700, color:bCol, minWidth:34 }}>{m.loadScore}</span>
                                          </div>
                                        </td>
                                        <td className="text-center"><Chip bg={m.overdue>0?"#fef2f2":T.bg} color={m.overdue>0?"#dc2626":T.text3}>{m.overdue}</Chip></td>
                                        <td className="text-center"><Chip bg={m.critical>0?"#fef2f2":T.bg} color={m.critical>0?"#dc2626":T.text3}>{m.critical}</Chip></td>
                                        <td className="text-center">
                                          <span style={{ fontSize:"0.78rem", fontWeight:600, color:m.daysActive>90?"#7c3aed":m.daysActive>=30?"#0284c7":T.text2 }}>
                                            {m.daysActive>0?`${m.daysActive}d`:"—"}
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span style={{ fontSize:"0.8rem", fontWeight:600, color:m.maxAge>30?"#dc2626":T.text2 }}>
                                            {m.maxAge>0?`${m.maxAge}d`:"—"}
                                          </span>
                                        </td>
                                        <td className="text-center"><Chip bg="#f0fdf4" color="#166534">{m.completed}</Chip></td>
                                      </tr>

                                      {isExp && (
                                        <tr style={{ background:"#fafbff" }}>
                                          <td colSpan={9} style={{ padding:"8px 16px 20px 56px", borderBottom:`2px solid ${T.primary}20` }}>
                                            <p style={{ fontSize:"0.76rem", color:T.text2, marginBottom:10, fontWeight:500 }}>
                                              <strong style={{ color:T.primary }}>{aTasks.length}</strong> open {aTasks.length===1?"task":"tasks"} — by priority then due date
                                            </p>
                                            {aTasks.length===0
                                              ? <p style={{ fontSize:"0.82rem", color:T.text3 }}>No open tasks.</p>
                                              : (
                                                <div className="table-responsive">
                                                  <table className="table table-borderless mb-0" style={{ fontSize:"0.8rem" }}>
                                                    <thead>
                                                      <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                                                        {["Task Code","Title","Project","Priority","Status","Due Date"].map((h,i) => (
                                                          <th key={i} style={{ fontSize:"0.65rem", fontWeight:700, color:T.text3, textTransform:"uppercase", letterSpacing:"0.05em", paddingBottom:8, whiteSpace:"nowrap" }}>{h}</th>
                                                        ))}
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {aTasks.map(t => {
                                                        const sb  = STATUS_BADGE[t.status]     || { bg:T.bg, color:T.text2 };
                                                        const pb  = PRIORITY_BADGE[t.priority] || { bg:T.bg, color:T.text2 };
                                                        const due = t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-GB",{day:"2-digit",month:"short"}) : "—";
                                                        const isOvr = t.dueDate && new Date(t.dueDate) < today;
                                                        return (
                                                          <tr key={t.id} style={{ borderBottom:`1px solid ${T.bg}` }}>
                                                            <td>
                                                              <a href={`${window.location.origin}/tasks?taskId=${t.id}`} target="_blank" rel="noopener noreferrer"
                                                                style={{ color:T.primary, fontWeight:700, textDecoration:"none", fontSize:"0.76rem" }}>
                                                                {t.taskCode||`#${t.id}`}
                                                              </a>
                                                            </td>
                                                            <td style={{ maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:T.text1, fontWeight:500 }}>{t.title}</td>
                                                            <td style={{ color:T.text2, whiteSpace:"nowrap" }}>{t.projectName||"—"}</td>
                                                            <td><span style={{ ...pb, borderRadius:20, padding:"2px 9px", fontSize:"0.71rem", fontWeight:700 }}>{t.priority||"—"}</span></td>
                                                            <td><span style={{ ...sb, borderRadius:20, padding:"2px 9px", fontSize:"0.71rem", fontWeight:600, whiteSpace:"nowrap" }}>{(t.status||"").replace(/_/g," ")}</span></td>
                                                            <td><span style={{ fontSize:"0.78rem", fontWeight:600, color:isOvr?"#dc2626":T.text2, whiteSpace:"nowrap" }}>{due}</span></td>
                                                          </tr>
                                                        );
                                                      })}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              )
                                            }
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )
                      }
                    </Card>
                  </>
                );
              })()}

            </div>
          )}
        </div>
      </div>

      <style>{`
        .mgr-scroll::-webkit-scrollbar { width: 6px; }
        .mgr-scroll::-webkit-scrollbar-track { background: transparent; }
        .mgr-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
        .mgr-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .mgr-scroll { scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent; }
      `}</style>
    </>
  );
};

export default ManagerTaskLanding;
